import type { ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useSelectedClientStore } from '@entities/shared/client';

// Query prefixes whose data does NOT depend on the selected client — kept across
// a switch. Session data (the user, their clients, permissions) plus client-agnostic
// reference data (product icon lookup, same for everyone). Everything else is
// client-scoped via the X-Client-Id header and must be dropped so the new client
// never reads the previous client's cached data.
const CLIENT_INDEPENDENT_QUERY_KEYS = [
  'accounts',
  'clients',
  'permissions',
  'product-lookup',
];

type SwitchClientProps = {
  render: (props: {
    selectedId: string | null;
    onSelect: (clientId: string) => void;
  }) => ReactNode;
};

// Pattern 2 (render-props, logic only): bridges the selected-client store to the
// switch action. Sets the new client (write-through to the interceptor), then
// removes the business cache. `removeQueries` (not `resetQueries`) is used on
// purpose: it drops the entries without force-refetching still-mounted observers.
// The <Outlet> remount (keyed on clientId) then unmounts the old subtree, so no
// stale request (e.g. a previous client's contract balance) is ever fired.
export const SwitchClient = (props: SwitchClientProps) => {
  const { render } = props;

  const queryClient = useQueryClient();
  const selectedClient = useSelectedClientStore();

  const handleSelect = (clientId: string) => {
    selectedClient.setClientId(clientId);
    queryClient.removeQueries({
      predicate: (query) => {
        const [prefix] = query.queryKey;
        return !CLIENT_INDEPENDENT_QUERY_KEYS.includes(String(prefix));
      },
    });
  };

  return <>{render({ selectedId: selectedClient.clientId, onSelect: handleSelect })}</>;
};
